import React from "react";
import { rupees } from "../rates/rateModel";
import { useKT } from "../i18n/kt";
import type { AttendanceRow } from "./attendanceRows";
import { SELF_NOTE } from "./attendanceRows";
import { hours, reasonLabel } from "./punchModel";

/** Desktop: everyone on one table, In / Out times, hours and time pay, one tap for In or Out. */
const AttendanceTable = ({
  rows,
  isToday,
  busyId,
  queued,
  newId,
  onOpen,
  onIn,
  onOut,
}: {
  rows: AttendanceRow[];
  isToday: boolean;
  busyId: string | null;
  queued: Set<string>;
  newId?: string | null;
  onOpen: (r: AttendanceRow) => void;
  onIn: (r: AttendanceRow) => void;
  onOut: (r: AttendanceRow) => void;
}) => {
  const { t } = useKT();
  return (
    <div className="kd-table-wrap">
      <table className="kd-table kat-table">
        <thead>
          <tr>
            <th scope="col">{t("Code")}</th>
            <th scope="col">{t("Name")}</th>
            <th scope="col">{t("Work")}</th>
            <th scope="col">{t("In")}</th>
            <th scope="col">{t("Out")}</th>
            <th scope="col" className="is-num">
              {t("Hours")} · ₹
            </th>
            <th scope="col" aria-label={t("Action")} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const notIn = r.kind === "NOT_YET";
            return (
              <tr key={r.personId} className={`is-${r.kind}${r.personId === newId ? " is-new" : ""}`}>
                <th scope="row">
                  <button type="button" className="kd-cell-link kw-code" onClick={() => onOpen(r)}>
                    {r.code}
                  </button>
                </th>
                <td>
                  {r.name}
                  {r.personId === newId ? <span className="kat-new">{t("NEW")}</span> : null}
                </td>
                <td>
                  {t(r.work)}
                  {r.loomCode ? ` · ${t("Loom")} ${r.loomCode}` : ""}
                </td>
                <td className={notIn ? "kd-dim" : ""}>{notIn ? t("not in") : r.firstIn || "—"}</td>
                <td>{r.kind === "OUT" ? `${r.lastOut || ""}${r.outReason ? ` ${t(reasonLabel(r.outReason)).toLowerCase()}` : ""}` : r.kind === "FIX" ? <span className="kd-chip is-warn">{t("Fix")}</span> : "—"}</td>
                <td className="is-num">
                  {notIn ? (
                    "—"
                  ) : r.noRate ? (
                    <span className="kd-chip is-warn">{t("No day rate")}</span>
                  ) : (
                    `${hours(r.minutes)}${r.pay && Number(r.pay) > 0 ? ` · ${rupees(r.pay, 0)}` : ""}`
                  )}
                </td>
                <td>
                  {r.self ? (
                    <span className="kd-chip" title={t(SELF_NOTE)}>
                      {t("You")} · {t("marked by others")}
                    </span>
                  ) : queued.has(r.personId) ? (
                    <span className="kd-chip is-info">{t("On phone")}</span>
                  ) : r.waiting ? (
                    <span className="kd-chip is-info">{t("Waiting")}</span>
                  ) : !isToday || r.kind === "FIX" ? (
                    <button type="button" className="kd-btn kd-btn--line kd-btn--xs" onClick={() => onOpen(r)}>
                      {r.kind === "FIX" ? t("Fix") : t("Open")}
                    </button>
                  ) : r.kind === "IN" ? (
                    <button type="button" className="kd-btn kd-btn--line kd-btn--xs" disabled={busyId === r.personId} onClick={() => onOut(r)}>
                      {t("Out")}
                    </button>
                  ) : (
                    <button type="button" className="kd-btn kd-btn--xs" disabled={busyId === r.personId} onClick={() => onIn(r)}>
                      {t("In")}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AttendanceTable;
